// src/components/ProfileSettings.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  IoLockClosedOutline,
  IoEyeOutline,
  IoEyeOffOutline,
  IoPersonOutline,
  IoMailOutline,
  IoShieldCheckmarkOutline,
  IoCheckmarkDoneOutline,
  IoInformationCircleOutline,
  IoServerOutline,
  IoPaperPlaneOutline,
  IoAlarmOutline,
} from 'react-icons/io5';
import { useAuthContext } from '../context/AuthContext';
import { itemVariants } from './AnimatedPage';
import api from '../api';

const container = {
  animate: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, icon: Icon, children }) => (
  <motion.div variants={itemVariants} className="glass-panel rounded-2xl overflow-hidden">
    <div className="px-5 py-4 border-b border-white/8 flex items-center gap-2">
      <Icon size={16} className="text-marizan-red" />
      <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-300">{title}</h2>
    </div>
    <div className="px-5 py-5 space-y-4">{children}</div>
  </motion.div>
);

// ─── Password field ───────────────────────────────────────────────────────────
const PwField = ({ label, value, onChange, show, onToggle, placeholder }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-600 pr-10 focus:outline-none focus:border-marizan-red/60 transition-colors"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
      >
        {show ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
      </button>
    </div>
  </div>
);

// ─── Change password section ──────────────────────────────────────────────────
const ChangePassword = () => {
  const [current, setCurrent]   = useState('');
  const [next, setNext]         = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showCur, setShowCur]   = useState(false);
  const [showNew, setShowNew]   = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error('Las contraseñas nuevas no coinciden.'); return; }
    if (next.length < 8)  { toast.error('La contraseña debe tener al menos 8 caracteres.'); return; }
    setLoading(true);
    try {
      await api.put('/me/password', { current_password: current, new_password: next });
      toast.success('Contraseña actualizada correctamente.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al cambiar la contraseña.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Cambiar contraseña" icon={IoLockClosedOutline}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <PwField
          label="Contraseña actual"
          value={current}
          onChange={e => setCurrent(e.target.value)}
          show={showCur}
          onToggle={() => setShowCur(p => !p)}
          placeholder="••••••••"
        />
        <PwField
          label="Nueva contraseña"
          value={next}
          onChange={e => setNext(e.target.value)}
          show={showNew}
          onToggle={() => setShowNew(p => !p)}
          placeholder="Mínimo 8 caracteres"
        />
        <PwField
          label="Confirmar nueva contraseña"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          show={showNew}
          onToggle={() => setShowNew(p => !p)}
          placeholder="Repite la nueva contraseña"
        />

        {/* Strength indicator */}
        {next.length > 0 && (
          <div className="flex gap-1 pt-1">
            {[1,2,3,4].map(i => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  next.length >= i * 3
                    ? next.length >= 12 ? 'bg-green-400' : next.length >= 8 ? 'bg-yellow-400' : 'bg-marizan-red'
                    : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !current || !next || !confirm}
          className="w-full py-2.5 rounded-xl bg-marizan-red hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2 mt-2"
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <IoCheckmarkDoneOutline size={16} />
          )}
          {loading ? 'Guardando…' : 'Actualizar contraseña'}
        </button>
      </form>
    </Section>
  );
};

// ─── Account info ─────────────────────────────────────────────────────────────
const AccountInfo = ({ user }) => (
  <Section title="Información de cuenta" icon={IoPersonOutline}>
    <div className="space-y-3">
      {[
        { icon: IoPersonOutline, label: 'Nombre', value: user?.name },
        { icon: IoMailOutline,   label: 'Email',  value: user?.email },
        { icon: IoShieldCheckmarkOutline, label: 'Rol', value: user?.role },
      ].map(({ icon: Icon, label, value }) => (
        <div key={label} className="flex items-center gap-3 bg-white/4 rounded-xl px-4 py-3">
          <Icon size={16} className="text-gray-400 shrink-0" />
          <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-sm text-gray-100 capitalize">{value || '—'}</p>
          </div>
        </div>
      ))}
    </div>
  </Section>
);

// ─── Security tips ────────────────────────────────────────────────────────────
const SecurityTips = () => (
  <Section title="Consejos de seguridad" icon={IoInformationCircleOutline}>
    <ul className="space-y-2 text-sm text-gray-400">
      {[
        'Usa una contraseña única que no uses en otros servicios.',
        'Mínimo 8 caracteres: mezcla letras, números y símbolos.',
        'No compartas tu contraseña con nadie.',
        'Cierra sesión en dispositivos que no sean tuyos.',
      ].map((tip, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-0.5 text-marizan-red shrink-0">•</span>
          {tip}
        </li>
      ))}
    </ul>
  </Section>
);

const ClientReminderSettings = () => {
  const [form, setForm] = useState({
    enabled: true,
    channel_email: true,
    workout_reminder_time: '18:00',
    measurement_frequency_days: 7,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/me/reminder-preferences');
        setForm({
          enabled: Boolean(res.data.enabled),
          channel_email: Boolean(res.data.channel_email),
          workout_reminder_time: res.data.workout_reminder_time || '18:00',
          measurement_frequency_days: res.data.measurement_frequency_days || 7,
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'No se pudieron cargar recordatorios');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/me/reminder-preferences', {
        ...form,
        measurement_frequency_days: Number(form.measurement_frequency_days),
      });
      toast.success('Preferencias de recordatorio actualizadas');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar recordatorios');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Recordatorios" icon={IoAlarmOutline}>
      {loading ? (
        <div className="skeleton h-24 rounded-xl" />
      ) : (
        <form onSubmit={save} className="space-y-3">
          <label className="flex items-center justify-between bg-white/4 rounded-xl px-4 py-3 text-sm text-gray-200">
            <span>Activar recordatorios</span>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
          </label>

          <label className="flex items-center justify-between bg-white/4 rounded-xl px-4 py-3 text-sm text-gray-200">
            <span>Enviar por email</span>
            <input
              type="checkbox"
              checked={form.channel_email}
              onChange={(e) => setForm({ ...form, channel_email: e.target.checked })}
            />
          </label>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Hora de recordatorio de entreno</label>
            <input
              type="time"
              value={form.workout_reminder_time}
              onChange={(e) => setForm({ ...form, workout_reminder_time: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Frecuencia recordatorio de medidas (días)</label>
            <input
              type="number"
              min={3}
              max={30}
              value={form.measurement_frequency_days}
              onChange={(e) => setForm({ ...form, measurement_frequency_days: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 rounded-xl bg-marizan-red hover:bg-rose-600 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
          >
            {saving ? 'Guardando...' : 'Guardar recordatorios'}
          </button>
        </form>
      )}
    </Section>
  );
};

const AdminSMTPSettings = () => {
  const [form, setForm] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_use_ssl: true,
    smtp_use_tls: false,
    email_from: '',
    public_app_url: 'https://fit.gzycode.com/',
  });
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');

  const loadSMTP = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/settings/smtp');
      const data = res.data;
      setForm({
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 465,
        smtp_user: data.smtp_user || '',
        smtp_password: '',
        smtp_use_ssl: Boolean(data.smtp_use_ssl),
        smtp_use_tls: Boolean(data.smtp_use_tls),
        email_from: data.email_from || '',
        public_app_url: data.public_app_url || 'https://fit.gzycode.com/',
      });
      setHasPassword(Boolean(data.has_password));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar SMTP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSMTP();
  }, []);

  const saveSMTP = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        smtp_port: Number(form.smtp_port),
        smtp_password: form.smtp_password || null,
      };
      const res = await api.put('/admin/settings/smtp', payload);
      setHasPassword(Boolean(res.data?.has_password));
      setForm((prev) => ({ ...prev, smtp_password: '' }));
      toast.success('SMTP guardado correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar SMTP');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) {
      toast.error('Indica un correo destino para la prueba');
      return;
    }
    setTesting(true);
    try {
      await api.post('/admin/settings/smtp/test', { to_email: testTo });
      toast.success('Correo de prueba enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar correo de prueba');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Section title="SMTP (Admin)" icon={IoServerOutline}>
      {loading ? (
        <div className="skeleton h-28 rounded-xl" />
      ) : (
        <form onSubmit={saveSMTP} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
              placeholder="SMTP Host"
              value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              required
            />
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
              placeholder="Puerto"
              type="number"
              value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
              required
            />
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
              placeholder="SMTP User"
              value={form.smtp_user}
              onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
              required
            />
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
              placeholder={hasPassword ? 'Nueva contraseña (dejar vacio para mantener)' : 'SMTP Password'}
              type="password"
              value={form.smtp_password}
              onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
            />
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100 md:col-span-2"
              placeholder="Email From"
              value={form.email_from}
              onChange={(e) => setForm({ ...form, email_from: e.target.value })}
              required
            />
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100 md:col-span-2"
              placeholder="URL pública del sitio (ej: https://tu-dominio.com/)"
              value={form.public_app_url}
              onChange={(e) => setForm({ ...form, public_app_url: e.target.value })}
              required
            />
          </div>

          <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.smtp_use_ssl}
                onChange={(e) => setForm({ ...form, smtp_use_ssl: e.target.checked })}
              />
              SSL
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.smtp_use_tls}
                onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })}
              />
              STARTTLS
            </label>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 rounded-xl bg-marizan-red hover:bg-rose-600 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
          >
            {saving ? 'Guardando...' : 'Guardar SMTP'}
          </button>

          <div className="border-t border-white/10 pt-3 space-y-2">
            <label className="block text-xs text-gray-400">Correo destino para prueba</label>
            <div className="flex gap-2">
              <input
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-gray-100"
                type="email"
                placeholder="correo@dominio.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <button
                type="button"
                onClick={sendTest}
                disabled={testing}
                className="px-4 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-sm font-semibold inline-flex items-center gap-1"
              >
                <IoPaperPlaneOutline size={14} /> {testing ? 'Enviando...' : 'Probar'}
              </button>
            </div>
          </div>
        </form>
      )}
    </Section>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────
const ProfileSettings = () => {
  const { user } = useAuthContext();

  return (
    <motion.div
      variants={container}
      initial="initial"
      animate="animate"
      className="container mx-auto px-4 py-8 max-w-lg space-y-5"
    >
      <motion.div variants={itemVariants} className="text-center space-y-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-marizan-red">Configuración</p>
        <h1 className="text-3xl font-bold text-white font-display">Preferencias</h1>
      </motion.div>

      <AccountInfo user={user} />
      {user?.role === 'admin' && <AdminSMTPSettings />}
      {user?.role === 'client' && <ClientReminderSettings />}
      <ChangePassword />
      <SecurityTips />
    </motion.div>
  );
};

export default ProfileSettings;